import { InventoryWebsiteVisibilityComponent } from '../../../inventory-website-visibility.component';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RmsModule } from '@rms';
import { WebService, lastValueFrom } from '@services';
import { RmsPulldownRef } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

interface ConversionPreview {
  bags: number;
  netWeight: number;
  totalWeight: number;
  typeName: string;
}

@Component({
  selector: 'app-convert-sku',
  standalone: true,
  imports: [InventoryWebsiteVisibilityComponent,
    CommonModule,
    RmsModule,
    FormsModule
  ],
  templateUrl: './convert-sku.component.html',
  styleUrl: './convert-sku.component.scss'
})
export class ConvertSkuComponent implements OnInit {
  WebInvisible = true;

  InventoryItem: any;

  CoffeeTypes: any[] = [];

  InventoryTypeID: number | null = null;
  BagsReconciled: number | null = null;
  ConversionReason = '';

  errorMessage: string | null = null;

  constructor(
    @Inject(RMS_PULLDOWN_DATA) private data: any,
    public ref: RmsPulldownRef,
    private webService: WebService
  ) {
    this.InventoryItem = data || {};
  }


  async ngOnInit(): Promise<void> {

    const allTypes: any[] =
      await lastValueFrom(
        this.webService.getWebCall(
          'inventory',
          'coffee/inventory-types'
        )
      );

    /*
     * Convert SKU only allows conversion into
     * General Purpose inventory types.
     */
    //this.CoffeeTypes =
    //  (allTypes ?? []).filter(
    //    type =>
    //      /\(GP\)/i.test(
    //        type?.InventoryTypeName ?? ''
    //      )
    //  );
    this.CoffeeTypes = allTypes ?? [];
  }


  get sourceTypeName(): string {
    return this.InventoryItem?.InventoryTypeName ||
      this.CoffeeTypes.find(type => Number(type.InventoryTypeID) === Number(this.InventoryItem?.InventoryTypeID))?.InventoryTypeName || 'SKU name unavailable';
  }

  get availableBags(): number {
    return Number(
      this.InventoryItem?.AvailableQty ?? 0
    );
  }


  get netWeight(): number {
    return Number(
      this.InventoryItem?.NetWeight ?? 0
    );
  }


  get availableWeight(): number {
    return this.availableBags * this.netWeight;
  }


  get selectedType(): any | null {

    if (!this.InventoryTypeID) {
      return null;
    }

    return this.CoffeeTypes.find(
      type =>
        type.InventoryTypeID ===
        this.InventoryTypeID
    ) ?? null;
  }


  get conversionPreview(): ConversionPreview | null {

    const bags =
      Number(this.BagsReconciled ?? 0);

    if (bags <= 0) {
      return null;
    }

    return {
      bags,

      netWeight:
        this.netWeight,

      totalWeight:
        this.round(
          bags * this.netWeight
        ),

      typeName:
        this.selectedType?.InventoryTypeName ?? ''
    };
  }


  get validationMessage(): string | null {

    const bags =
      Number(this.BagsReconciled ?? 0);

    /*
     * Don't display an error before the
     * user enters a quantity.
     */
    if (bags <= 0) {
      return null;
    }

    if (bags > this.availableBags) {
      return `This conversion requires ${bags} bag(s), but only ${this.availableBags} bag(s) are available on this ref#.`;
    }

    return null;
  }


  get canSave(): boolean {
    if (!this.ConversionReason.trim() || this.ConversionReason.length > 2000) return false;

    if (!this.InventoryTypeID) {
      return false;
    }

    if (
      !this.BagsReconciled ||
      this.BagsReconciled <= 0
    ) {
      return false;
    }

    return this.validationMessage === null;
  }


  save(): void {

    this.errorMessage = null;
    if (!this.ConversionReason.trim() || this.ConversionReason.length > 2000) {
      this.errorMessage = 'Enter a conversion reason (up to 2,000 characters).';
      return;
    }

    if (!this.InventoryTypeID) {
      this.errorMessage =
        'Select a coffee type to convert to.';
      return;
    }

    if (
      !this.BagsReconciled ||
      this.BagsReconciled <= 0
    ) {
      this.errorMessage =
        'Enter a valid number of bags to recondition.';
      return;
    }

    const validationError =
      this.validationMessage;

    if (validationError) {
      this.errorMessage =
        validationError;
      return;
    }

    const selectedType =
      this.selectedType;

    this.ref.close({
      cancelled: false,
      WebInvisible: this.WebInvisible,
      ConversionReason: this.ConversionReason.trim(),

      InventoryTypeID:
        this.InventoryTypeID,

      InventoryTypeName:
        selectedType?.InventoryTypeName ?? '',

      BagsReconciled:
        this.BagsReconciled,

      NetWeight:
        this.netWeight,

      TotalWeight:
        this.round(
          this.BagsReconciled *
          this.netWeight
        )
    });
  }


  cancel(): void {
    this.ref.close({
      cancelled: true
    });
  }


  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
